"use client";

import { useState } from "react";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import {
  CopPanel,
  EnginePanel,
  FlowPanel,
} from "@/components/tools/physics-fields/carnot-panels";
import {
  type SigChoice,
  SigFigsField,
} from "@/components/tools/physics-fields/shared";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";

/** English strings. A translated content file overrides these through `ui`. */
export const CARNOT_EFFICIENCY_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to work out?",
  modeEngine: "Maximum efficiency of a heat engine",
  modeCop: "Refrigerator or heat pump (COP)",
  modeFlows: "Check an engine's heat flows",
  exampleLabel: "Start from an example",
  custom: "Custom values",
  engineExamples: {
    plant: "A power plant: 540 °C steam, 25 °C cooling water",
    kelvin: "Reservoirs at 600 K and 300 K",
    steam: "Boiling and freezing water: 100 °C and 0 °C",
    hot: "What hot temperature gives 40% with a 300 K sink?",
  },
  copExamples: {
    fridge: "A refrigerator: 4 °C inside a 25 °C kitchen",
    freezer: "A freezer: −18 °C inside a 25 °C kitchen",
    pump: "A heat pump: 20 °C indoors, 0 °C outdoors, 1 kWh of work",
  },
  flowExamples: {
    allowed: "An engine takes 1000 J at 600 K and rejects 700 J at 300 K",
    impossible: "A claim of 600 J of work from 1000 J between 600 K and 300 K",
    carnot: "A reversible engine: 500 J of work from 1000 J",
  },
  solveFor: "Solve for",
  unitFor: "Unit for {name}",
  hot: "Hot reservoir temperature T_h",
  cold: "Cold reservoir temperature T_c",
  hotShort: "Hot temperature T_h",
  coldShort: "Cold temperature T_c",
  efficiency: "Efficiency η",
  efficiencyShort: "Efficiency η",
  hotSide: "Hot side temperature T_h",
  coldSide: "Cold side temperature T_c",
  workInput: "Work input W (optional)",
  workInputHint: "Adds how much heat is moved for that work.",
  heatAbsorbed: "Heat absorbed from the hot reservoir Q_h",
  heatRejected: "Heat rejected to the cold reservoir Q_c",
  workDone: "Work done W",
  knownLabel: "What do you know besides Q_h?",
  knownRejected: "The heat rejected, Q_c",
  knownWork: "The work done, W",
  sigFigs: "Significant figures",
  sigFigsMessage:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  maxEfficiency: "Maximum (Carnot) efficiency",
  workPer100: "Most work from 100 J of heat",
  rejectedPer100: "Heat rejected per 100 J absorbed",
  temperatureGap: "Temperature difference",
  copCooling: "COP as a refrigerator",
  copHeating: "COP as a heat pump",
  heatRemoved: "Heat removed from the cold side",
  heatDelivered: "Heat delivered to the hot side",
  actualEfficiency: "Engine's efficiency η = W/Q_h",
  shareOfCarnot: "Share of the Carnot limit",
  maxWork: "Most work possible from this heat",
  entropyGenerated: "Entropy generated per cycle",
  violates:
    "This engine would beat the Carnot limit, which the second law of thermodynamics forbids. Check the numbers: either more heat must be rejected, or the reservoirs must be further apart in temperature.",
  reversible:
    "This engine matches the Carnot limit: it is reversible, generates no entropy and is the best any engine between these temperatures can do.",
  irreversible:
    "This engine is allowed by the second law: its efficiency is below the Carnot limit and it generates entropy, as every real engine does.",
  working: "The working",
  engineLabel: "A heat engine between two reservoirs",
  engineCaption:
    "Heat Q_h flows in from the hot reservoir, part leaves as work W and the rest, Q_c, is rejected to the cold reservoir. Arrow thickness shows each flow's share of Q_h.",
  engineDescription:
    "A hot reservoir above and a cold reservoir below with a machine between them. A thick arrow carries heat down into the machine, a thinner arrow carries work out to the right, and another arrow carries rejected heat down to the cold reservoir.",
  coolerLabel: "A refrigerator or heat pump between two reservoirs",
  coolerCaption:
    "Work W drives heat Q_c up from the cold side to the hot side, which receives Q_h = Q_c + W. Arrow thickness shows each flow's share of Q_h.",
  coolerDescription:
    "A hot reservoir above and a cold reservoir below with a machine between them. An arrow carries heat up from the cold reservoir into the machine, an arrow carries work in from the right, and a thicker arrow carries heat up to the hot reservoir.",
  errorMissing:
    "Fill in every box except the one you are solving for. Temperatures can be in kelvin, °C, °F or °R.",
  errorAbsoluteZero:
    "A temperature must be above absolute zero (0 K, −273.15 °C or −459.67 °F): reservoirs at or below it cannot exist.",
  errorColdAboveHot:
    "The cold reservoir must not be hotter than the hot one. Swap the two temperatures.",
  errorEqual:
    "With no temperature difference the coefficient of performance is infinite. Enter two different temperatures.",
  errorEfficiency:
    "An efficiency must be at least 0% and below 100%: a Carnot engine reaches 100% only with a cold reservoir at absolute zero.",
  errorPositive:
    "The heat and the work must be greater than zero, with the rejected heat or work not negative.",
  errorFlows:
    "The work and the rejected heat cannot exceed the heat absorbed: by the first law, Q_h = W + Q_c.",
  errorRange:
    "Those numbers are too large or too small to calculate. Check the units.",
  assumptions:
    "Assumes ideal, reversible Carnot cycles between two constant-temperature reservoirs, with temperatures in absolute units (the calculator converts °C and °F for you). Carnot values are upper limits that no real engine reaches: friction, finite heat-transfer rates and imperfect insulation all lower the real efficiency.",
} as const;

type Mode = "engine" | "cop" | "flows";

export default function CarnotEfficiencyCalculatorWidget() {
  const strings = useToolUi(CARNOT_EFFICIENCY_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("engine");
  const [sigChoice, setSigChoice] = useState<SigChoice>("4");
  const sig = Number(sigChoice);

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="ce-mode"
        onChange={setMode}
        options={[
          { value: "engine", label: strings.modeEngine },
          { value: "cop", label: strings.modeCop },
          { value: "flows", label: strings.modeFlows },
        ]}
        value={mode}
      />
      {mode === "engine" ? <EnginePanel sig={sig} strings={strings} /> : null}
      {mode === "cop" ? <CopPanel sig={sig} strings={strings} /> : null}
      {mode === "flows" ? <FlowPanel sig={sig} strings={strings} /> : null}
      <SigFigsField
        format={fmt}
        id="ce-sig"
        label={strings.sigFigs}
        locale={locale}
        message={strings.sigFigsMessage}
        onChange={setSigChoice}
        value={sigChoice}
      />
      <Notice>{strings.assumptions}</Notice>
    </>
  );
}
